import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Avatar, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { Cake, Celebration, CardGiftcard } from '@mui/icons-material';
import { newsAPI } from '../../services/api';
import { getViewerTimezone } from '../../utils/dateTimeUtils';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_LABELS = {
  teacher: 'Teacher',
  teacher_supervisor: 'Teacher supervisor',
  teacher_admin: 'Teacher admin',
  sales: 'Sales',
  sales_supervisor: 'Sales supervisor',
  country_manager: 'Country Manager',
  admin: 'Admin',
  finance_user: 'Finance',
  finance_supervisor: 'Finance supervisor',
  finance_admin: 'Finance admin',
  batch_supervisor: 'Batch supervisor',
  batch_admin: 'Batch admin',
  parent_feedback_agent: 'Parent feedback',
};

const AVATAR_COLORS = ['#E91E63', '#FF7043', '#AB47BC', '#26A69A', '#42A5F5', '#FFB300'];

function initials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '🎉';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function avatarColor(id) {
  const s = String(id || '');
  let n = 0;
  for (let i = 0; i < s.length; i += 1) n += s.charCodeAt(i);
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
}

function whenLabel(daysUntil, occurrenceDate) {
  if (daysUntil === 0) return 'Today 🎉';
  if (daysUntil === 1) return 'Tomorrow';
  if (!occurrenceDate) return '';
  const [y, m, d] = String(occurrenceDate).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function BirthdayCard({ item, onOpen, dense = false }) {
  const isToday = item.daysUntil === 0;
  const roleLabel = item.kind === 'employee' ? ROLE_LABELS[item.role] || item.role : 'Student';
  const showAge = item.kind === 'student' && item.turningAge != null;

  return (
    <Box
      onClick={onOpen}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: dense ? 1 : 1.25,
        mb: dense ? 0 : 1,
        minWidth: dense ? 240 : undefined,
        flexShrink: dense ? 0 : undefined,
        borderRadius: 2.5,
        cursor: onOpen ? 'pointer' : 'default',
        background: isToday
          ? 'linear-gradient(135deg, #fff7e6 0%, #ffe4ef 100%)'
          : '#fff',
        border: isToday ? '1px solid #ffc107' : '1px solid rgba(233, 30, 99, 0.12)',
        boxShadow: isToday ? '0 4px 14px rgba(255, 152, 0, 0.18)' : 'none',
        '&:last-child': { mb: 0 },
        '&:hover': onOpen ? { transform: 'translateY(-1px)', boxShadow: 2 } : undefined,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          fontSize: 14,
          fontWeight: 700,
          bgcolor: avatarColor(item.id),
          flexShrink: 0,
        }}
      >
        {initials(item.name)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {isToday ? `🎂 ${item.name}` : item.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {isToday
            ? `Happy Birthday${showAge ? ` · ${item.turningAge}` : ''}!`
            : `${roleLabel}${showAge ? ` · turning ${item.turningAge}` : ''}`}
        </Typography>
      </Box>
      <Chip
        size="small"
        icon={isToday ? <Celebration sx={{ fontSize: 14 }} /> : <Cake sx={{ fontSize: 14 }} />}
        label={whenLabel(item.daysUntil, item.occurrenceDate)}
        sx={{
          flexShrink: 0,
          fontWeight: 700,
          bgcolor: isToday ? '#ff6b9d' : '#fff3e0',
          color: isToday ? '#fff' : '#e65100',
          '& .MuiChip-icon': { color: isToday ? '#fff' : '#e65100' },
        }}
      />
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 800,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: '#ad1457',
        mb: 0.75,
        mt: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

export default function NewsUpdatesPanel({ compact = false, layout = 'stack' }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const isRibbon = layout === 'ribbon';
  const limit = isRibbon ? 8 : compact ? 6 : 50;

  const birthdaysQuery = useQuery(
    ['newsBirthdays', viewerTimezone],
    async () => {
      const res = await newsAPI.getUpcomingBirthdays({ days: 7, viewer_timezone: viewerTimezone });
      return res.data?.data || { students: [], employees: [] };
    },
    { staleTime: 60000 }
  );

  const updatesQuery = useQuery(
    ['newsUpdates'],
    async () => {
      const res = await newsAPI.getUpdates();
      return res.data?.data || [];
    },
    { staleTime: 60000 }
  );

  const students = (birthdaysQuery.data?.students || []).slice(0, limit);
  const employees = (birthdaysQuery.data?.employees || []).slice(0, limit);
  const people = [...students, ...employees];
  const updates = updatesQuery.data || [];
  const empty = people.length === 0;

  if (isRibbon) {
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid rgba(233, 30, 99, 0.18)',
          minHeight: 76,
        }}
      >
        <Box
          sx={{
            px: 1.75,
            py: 1.25,
            minWidth: 148,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            background: 'linear-gradient(165deg, #ff6b9d 0%, #ff8e53 100%)',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Cake sx={{ color: '#fff', fontSize: 22 }} />
            <Typography variant="subtitle2" fontWeight={800} color="#fff" lineHeight={1.2}>
              Birthdays
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.25 }}>
            Next 7 days
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            overflowX: 'auto',
            bgcolor: '#fff8fb',
          }}
        >
          {birthdaysQuery.isLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading celebrations…
            </Typography>
          ) : empty ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CardGiftcard sx={{ color: '#f48fb1' }} />
              <Typography variant="body2" color="text.secondary">
                No birthday celebrations this week.
              </Typography>
            </Stack>
          ) : (
            people.map((item) => (
              <BirthdayCard
                key={`${item.kind}-${item.id}`}
                item={item}
                dense
                onOpen={item.kind === 'student' ? () => navigate('/students') : undefined}
              />
            ))
          )}
        </Box>

        <Stack
          spacing={0.5}
          justifyContent="center"
          sx={{
            px: 1.5,
            py: 1,
            flexShrink: 0,
            maxWidth: 200,
            borderLeft: '1px solid rgba(233, 30, 99, 0.12)',
            bgcolor: '#fff',
          }}
        >
          <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Updates
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {updates[0]?.title || 'No announcements yet'}
          </Typography>
          <Button size="small" onClick={() => navigate('/news')} sx={{ alignSelf: 'flex-start', fontWeight: 700, px: 0 }}>
            See all
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid rgba(233, 30, 99, 0.18)',
          background: 'linear-gradient(180deg, #fff5f8 0%, #ffffff 42%)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            px: 2,
            py: 1.75,
            background: 'linear-gradient(135deg, #ff6b9d 0%, #ff8e53 55%, #ffc371 100%)',
            overflow: 'hidden',
          }}
        >
          {[
            { left: 12, top: 8, size: 8, color: 'rgba(255,255,255,0.55)' },
            { left: 48, top: 28, size: 6, color: '#ffe082' },
            { right: 28, top: 10, size: 7, color: 'rgba(255,255,255,0.7)' },
            { right: 56, top: 32, size: 5, color: '#f48fb1' },
            { left: '42%', top: 6, size: 5, color: '#fff59d' },
          ].map((dot, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: dot.size,
                height: dot.size,
                borderRadius: '50%',
                bgcolor: dot.color,
                left: dot.left,
                right: dot.right,
                top: dot.top,
              }}
            />
          ))}
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.22)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Cake sx={{ color: '#fff' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={800} color="#fff" noWrap>
                  Birthday celebrations
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Next 7 days
                </Typography>
              </Box>
            </Stack>
            {compact ? (
              <Button
                size="small"
                onClick={() => navigate('/news')}
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
                }}
              >
                See all
              </Button>
            ) : null}
          </Stack>
        </Box>

        <Box sx={{ p: 1.75 }}>
          {birthdaysQuery.isLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading celebrations…
            </Typography>
          ) : empty ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CardGiftcard sx={{ fontSize: 36, color: '#f48fb1', mb: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                No birthday celebrations in the next 7 days.
              </Typography>
            </Box>
          ) : (
            <>
              {students.length > 0 ? (
                <Box sx={{ mb: employees.length > 0 ? 1.5 : 0 }}>
                  <SectionLabel>Students</SectionLabel>
                  {students.map((item) => (
                    <BirthdayCard
                      key={`s-${item.id}`}
                      item={item}
                      onOpen={item.kind === 'student' ? () => navigate('/students') : undefined}
                    />
                  ))}
                </Box>
              ) : null}
              {employees.length > 0 ? (
                <Box>
                  <SectionLabel>Team</SectionLabel>
                  {employees.map((item) => (
                    <BirthdayCard key={`e-${item.id}`} item={item} />
                  ))}
                </Box>
              ) : null}
            </>
          )}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Updates
        </Typography>
        {updates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No announcements yet. Company news will appear here.
          </Typography>
        ) : (
          updates.slice(0, compact ? 4 : 20).map((u) => (
            <Box key={u.id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" fontWeight={600}>
                {u.title}
              </Typography>
              {u.body ? (
                <Typography variant="caption" color="text.secondary">
                  {u.body}
                </Typography>
              ) : null}
            </Box>
          ))
        )}
      </Paper>
    </Stack>
  );
}
